/**
 * Formatage d'une cellule du drill-down — module PUR, partagé entre l'AFFICHAGE (`CellValue`
 * dans `index.tsx`) et l'EXPORT (`exportRows.ts`).
 *
 * ⚠️ Une seule source de vérité, et c'est volontaire : si l'export refaisait son propre
 * formatage, le fichier livré divergerait de l'écran (`true` là où l'on voit ✓,
 * `[object Object]` là où l'on voit le libellé d'une relation, une date ISO là où l'on voit
 * `jj-mm-aaaa`) sans que rien ne le signale. Toute évolution du rendu doit passer par ici.
 */

/**
 * Heuristique de détection de date, appliquée quand le schéma ne déclare PAS la colonne en
 * `date` : ISO (`2026-08-28T…`) ou timestamp epoch (10 chiffres = secondes, 13 = millisecondes).
 *
 * ⚠️ Conséquence assumée : un nombre brut à 10-13 chiffres est lu comme un timestamp. C'est le
 * comportement de l'affichage depuis l'origine ; l'export s'y aligne plutôt que d'inventer une
 * seconde règle.
 */
export function looksLikeDate(val: any): boolean {
  if (typeof val !== "string" && typeof val !== "number") return false
  const s = String(val)
  if (/^\d{4}-\d{2}-\d{2}(T|\s)/.test(s)) return true
  if (/^\d{10,13}$/.test(s)) return true
  return false
}

/** Rend une date au format `jj-mm-aaaa`. Renvoie la valeur brute si elle n'est pas parsable. */
export function formatDate(val: any): string {
  const s = String(val)
  let d: Date
  if (/^\d{10}$/.test(s)) d = new Date(Number(s) * 1000)
  else if (/^\d{13}$/.test(s)) d = new Date(Number(s))
  else d = new Date(s)
  if (isNaN(d.getTime())) return s
  const dd = String(d.getDate()).padStart(2, "0")
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const yyyy = d.getFullYear()
  return `${dd}-${mm}-${yyyy}`
}

/**
 * Libellé d'une valeur objet (relation, utilisateur, option…). Renvoie `null` quand l'objet ne
 * porte rien d'affichable — l'appelant décide alors du rendu (« — » à l'écran, cellule vide
 * dans le fichier).
 *
 * Les clés techniques (`_id`, `__typename`…) sont ignorées.
 */
export function resolveObjectLabel(val: Record<string, any>): string | null {
  const entries = Object.entries(val).filter(([k]) => !k.startsWith("_"))
  if (entries.length === 0) return null
  const label = val.label ?? val.name ?? val.value ?? entries[0][1]
  if (label === null || label === undefined) return null
  if (typeof label === "object") return String(entries[0][1])
  return String(label)
}

/**
 * Valeur d'une cellule prête à être écrite dans un CSV / XLSX.
 *
 * Deux écarts DÉLIBÉRÉS avec l'affichage :
 * - les tableaux ne sont **pas** tronqués à 3 éléments (l'écran affiche `+N` faute de place ;
 *   un export qui perdrait des valeurs en silence serait faux) ;
 * - les nombres et booléens restent des primitives, pour rester triables et filtrables dans
 *   Excel au lieu d'arriver en texte.
 */
export function formatCellForExport(val: any, isDateCol?: boolean): string | number | boolean {
  if (val === null || val === undefined || val === "") return ""
  if (typeof val === "boolean") return val

  if (Array.isArray(val)) {
    if (val.length === 0) return ""
    return val
      .map(v => {
        if (v === null || v === undefined) return ""
        if (typeof v === "object") return resolveObjectLabel(v) ?? ""
        return String(v)
      })
      .filter(s => s !== "")
      .join(", ")
  }

  if (typeof val === "object") return resolveObjectLabel(val) ?? ""

  if (isDateCol || looksLikeDate(val)) return formatDate(val)
  if (typeof val === "number") return val
  return String(val)
}
